import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  InputAdornment,
  Checkbox,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Extension,
  MusicNote,
  Speed,
  AutoAwesome,
  DragIndicator,
  Close,
  Analytics,
  PlaylistAdd,
  Warning,
  CheckCircle,
  ContentCopy,
} from '@mui/icons-material';
import { Reorder, useDragControls } from 'framer-motion';
import { useSegments, useCreateSegment, useUpdateSegment, useDeleteSegment, useSegment, useAutoFillSegment, useSegmentAnalysis, useAddTrackToSegment, useRemoveTrackFromSegment, useReorderSegmentTracks, useDuplicateSegment } from '../../hooks/useScheduling';
import { DuplicateToOrgDialog } from '../../components/DuplicateToOrgDialog';
import { useContentList } from '../../hooks/useContent';
import type { Segment } from '../../api/types';
import { ContentWorkflowState, ContentType } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { SCHEDULING_TABS, SCHEDULING_ACCENT_COLOR } from './schedulingTabs';
import { schedulingHelp } from '../../components/PageHelp/helpContent';
import { useOrgFilter } from '../../contexts/OrganizationContext';

const MOOD_OPTIONS = [
  'calm', 'energetic', 'happy', 'melancholic', 'uplifting', 'relaxing',
  'intense', 'romantic', 'nostalgic', 'epic', 'chill', 'groovy'
];

const TEXTURE_OPTIONS = [
  { value: 'acoustic', label: 'Acoustic' },
  { value: 'electronic', label: 'Electronic' },
  { value: 'mixed', label: 'Mixed' },
];

const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
];

const SegmentsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatingSegment, setDuplicatingSegment] = useState<Segment | null>(null);

  // Use global organization filter
  const orgFilter = useOrgFilter();

  const { data: segments, isLoading, error } = useSegments({
    organization_id: orgFilter,
    search: searchQuery || undefined,
  });

  const createMutation = useCreateSegment();
  const updateMutation = useUpdateSegment();
  const deleteMutation = useDeleteSegment();
  const duplicateMutation = useDuplicateSegment();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#10b981',
    target_duration_minutes: 30,
    bpm_min: 80,
    bpm_max: 120,
    energy_level: 5,
    mood_tags: [] as string[],
    texture: 'mixed' as 'acoustic' | 'electronic' | 'mixed',
    explicit_allowed: false,
  });

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      description: '',
      color: '#10b981',
      target_duration_minutes: 30,
      bpm_min: 80,
      bpm_max: 120,
      energy_level: 5,
      mood_tags: [],
      texture: 'mixed',
      explicit_allowed: false,
    });
    setEditingSegment(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (segment: Segment) => {
    setFormData({
      name: segment.name,
      description: segment.description || '',
      color: segment.color,
      target_duration_minutes: segment.target_duration_minutes || 30,
      bpm_min: segment.bpm_min || 80,
      bpm_max: segment.bpm_max || 120,
      energy_level: segment.energy_level || 5,
      mood_tags: segment.mood_tags || [],
      texture: segment.texture || 'mixed',
      explicit_allowed: segment.explicit_allowed,
    });
    setEditingSegment(segment);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    // For creating new segments, require an organization to be selected
    if (!editingSegment && !orgFilter) {
      alert('Please select an organization from the header filter to create a new segment.');
      return;
    }

    try {
      if (editingSegment) {
        await updateMutation.mutateAsync({
          id: editingSegment.id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync({
          ...formData,
          organization_id: orgFilter!,
        });
      }
      setCreateDialogOpen(false);
    } catch (err) {
      console.error('Failed to save segment:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this segment?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleOpenDuplicate = (segment: Segment) => {
    setDuplicatingSegment(segment);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicate = async (params: { name?: string; targetOrganizationId?: string }) => {
    if (!duplicatingSegment) return;
    await duplicateMutation.mutateAsync({
      segmentId: duplicatingSegment.id,
      name: params.name,
      targetOrganizationId: params.targetOrganizationId,
    });
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load segments: {(error as Error)?.message}
      </Alert>
    );
  }

  return (
    <Box>
      <SectionTabs
        title="Scheduling"
        tabs={SCHEDULING_TABS}
        accentColor={SCHEDULING_ACCENT_COLOR}
        help={schedulingHelp.segments}
        actionButton={{
          label: 'New Segment',
          icon: <Add />,
          onClick: handleOpenCreate,
        }}
      />

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search segments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          size="small"
        />
      </Paper>

      {/* Segments Grid */}
      <Grid container spacing={2}>
        {segments?.map((segment) => (
          <Grid item xs={12} sm={6} md={4} key={segment.id}>
            <Card
              sx={{
                cursor: 'pointer',
                border: '2px solid',
                borderColor: segment.color,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}
              onClick={() => setSelectedSegmentId(segment.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Extension sx={{ color: segment.color }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {segment.name}
                  </Typography>
                  <Tooltip title="Duplicate to Organization">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDuplicate(segment);
                      }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEdit(segment);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(segment.id);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                {segment.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {segment.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  <Chip
                    size="small"
                    icon={<MusicNote />}
                    label={`${segment.track_count} tracks`}
                    sx={{ bgcolor: `${segment.color}20`, color: segment.color }}
                  />
                  <Chip
                    size="small"
                    icon={<Speed />}
                    label={`${segment.bpm_min || '?'}-${segment.bpm_max || '?'} BPM`}
                  />
                  {segment.energy_level && (
                    <Chip
                      size="small"
                      label={`Energy: ${segment.energy_level}/10`}
                    />
                  )}
                </Box>

                {segment.mood_tags && segment.mood_tags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {segment.mood_tags.slice(0, 3).map((mood) => (
                      <Chip key={mood} size="small" label={mood} variant="outlined" />
                    ))}
                    {segment.mood_tags.length > 3 && (
                      <Chip size="small" label={`+${segment.mood_tags.length - 3}`} variant="outlined" />
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Target: {segment.target_duration_minutes || 30} min
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Actual: {formatDuration(segment.actual_duration_seconds || 0)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {segments?.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Extension sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No segments yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create your first segment to start building atmosphere arcs
              </Typography>
              <Button variant="contained" onClick={handleOpenCreate}>
                Create Segment
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSegment ? 'Edit Segment' : 'Create New Segment'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {COLOR_OPTIONS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: color,
                      cursor: 'pointer',
                      border: formData.color === color ? '3px solid white' : 'none',
                      boxShadow: formData.color === color ? `0 0 0 2px ${color}` : 'none',
                    }}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              label="Target Duration (minutes)"
              type="number"
              value={formData.target_duration_minutes}
              onChange={(e) => setFormData({ ...formData, target_duration_minutes: parseInt(e.target.value) || 30 })}
              fullWidth
              inputProps={{ min: 5, max: 120 }}
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>BPM Range</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Min BPM"
                  type="number"
                  value={formData.bpm_min}
                  onChange={(e) => setFormData({ ...formData, bpm_min: parseInt(e.target.value) || 80 })}
                  size="small"
                  inputProps={{ min: 40, max: 200 }}
                />
                <TextField
                  label="Max BPM"
                  type="number"
                  value={formData.bpm_max}
                  onChange={(e) => setFormData({ ...formData, bpm_max: parseInt(e.target.value) || 120 })}
                  size="small"
                  inputProps={{ min: 40, max: 200 }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Energy Level: {formData.energy_level}/10
              </Typography>
              <Slider
                value={formData.energy_level}
                onChange={(_, value) => setFormData({ ...formData, energy_level: value as number })}
                min={1}
                max={10}
                marks
                valueLabelDisplay="auto"
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Texture</InputLabel>
              <Select
                value={formData.texture}
                label="Texture"
                onChange={(e) => setFormData({ ...formData, texture: e.target.value as any })}
              >
                {TEXTURE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Mood Tags</InputLabel>
              <Select
                multiple
                value={formData.mood_tags}
                label="Mood Tags"
                onChange={(e) => setFormData({ ...formData, mood_tags: e.target.value as string[] })}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {MOOD_OPTIONS.map((mood) => (
                  <MenuItem key={mood} value={mood}>{mood}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
          >
            {editingSegment ? 'Save Changes' : 'Create Segment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Segment Detail Dialog */}
      {selectedSegmentId && (
        <SegmentDetailDialog
          segmentId={selectedSegmentId}
          onClose={() => setSelectedSegmentId(null)}
        />
      )}

      {/* Duplicate to Organization Dialog */}
      {duplicatingSegment && (
        <DuplicateToOrgDialog
          open={duplicateDialogOpen}
          onClose={() => {
            setDuplicateDialogOpen(false);
            setDuplicatingSegment(null);
          }}
          entityType="segment"
          entityId={duplicatingSegment.id}
          entityName={duplicatingSegment.name}
          currentOrganizationId={duplicatingSegment.organization_id}
          onDuplicate={handleDuplicate}
        />
      )}
    </Box>
  );
};

// Draggable Track Item Component
const DraggableTrackItem: React.FC<{
  track: any;
  segment: any;
  onRemove: (trackId: string) => void;
  isRemoving: boolean;
  formatDuration: (seconds: number) => string;
}> = ({ track, segment, onRemove, isRemoving, formatDuration }) => {
  const dragControls = useDragControls();

  // Check if track is outside segment BPM range
  const bpmWarning = segment.bpm_min && segment.bpm_max && track.bpm &&
    (track.bpm < segment.bpm_min || track.bpm > segment.bpm_max);

  // Check if track energy is outside segment energy level (±1 tolerance)
  const energyWarning = segment.energy_level && track.energy_level &&
    Math.abs(track.energy_level - segment.energy_level) > 1;

  return (
    <Reorder.Item
      value={track}
      dragListener={false}
      dragControls={dragControls}
      style={{ listStyle: 'none' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.paper',
          mb: 0.5,
          borderRadius: 1,
          p: 1,
          border: (bpmWarning || energyWarning) ? '1px solid' : 'none',
          borderColor: 'warning.main',
        }}
      >
        <Box
          onPointerDown={(e) => dragControls.start(e)}
          sx={{ cursor: 'grab', mr: 1, touchAction: 'none' }}
        >
          <DragIndicator sx={{ color: 'text.secondary' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            {track.title || 'Unknown'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {track.artist || 'Unknown Artist'} - {formatDuration(track.duration || 0)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
          {track.bpm && (
            <Tooltip title={bpmWarning ? `BPM outside range ${segment.bpm_min}-${segment.bpm_max}` : ''}>
              <Chip
                size="small"
                label={`${Math.round(track.bpm)} BPM`}
                color={bpmWarning ? 'warning' : 'default'}
                icon={bpmWarning ? <Warning fontSize="small" /> : undefined}
              />
            </Tooltip>
          )}
          {track.energy_level && (
            <Tooltip title={energyWarning ? `Energy outside target ${segment.energy_level}` : ''}>
              <Chip
                size="small"
                label={`E${track.energy_level}`}
                color={energyWarning ? 'warning' : 'default'}
              />
            </Tooltip>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => onRemove(track.id)}
          disabled={isRemoving}
          sx={{ color: 'error.main' }}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    </Reorder.Item>
  );
};

// Add Track Dialog Component
const AddTrackDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  segmentId: string;
  segment: any;
  existingTrackIds: string[];
}> = ({ open, onClose, segmentId, segment, existingTrackIds }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTracks, setSelectedTracks] = useState<string[]>([]);

  const addTrackMutation = useAddTrackToSegment();

  // Fetch content with filters based on segment criteria
  const { data: contentList, isLoading } = useContentList({
    filters: {
      search: searchQuery || undefined,
      workflow_state: [ContentWorkflowState.APPROVED],
      content_type: [ContentType.AUDIO],
    },
    size: 50,
  });

  // Filter out tracks already in segment
  const availableTracks = contentList?.items?.filter(
    (track: any) => !existingTrackIds.includes(track.id)
  ) || [];

  const handleToggleTrack = (trackId: string) => {
    setSelectedTracks(prev =>
      prev.includes(trackId)
        ? prev.filter(id => id !== trackId)
        : [...prev, trackId]
    );
  };

  const handleAddSelected = async () => {
    for (const trackId of selectedTracks) {
      await addTrackMutation.mutateAsync({
        segmentId,
        contentId: trackId,
      });
    }
    setSelectedTracks([]);
    onClose();
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if track matches segment criteria
  const getMatchStatus = (track: any) => {
    const issues: string[] = [];

    if (segment.bpm_min && track.metadata?.tempo && track.metadata.tempo < segment.bpm_min) {
      issues.push(`BPM ${Math.round(track.metadata.tempo)} below min ${segment.bpm_min}`);
    }
    if (segment.bpm_max && track.metadata?.tempo && track.metadata.tempo > segment.bpm_max) {
      issues.push(`BPM ${Math.round(track.metadata.tempo)} above max ${segment.bpm_max}`);
    }
    if (segment.energy_level && track.metadata?.energy_level) {
      const diff = Math.abs(track.metadata.energy_level - segment.energy_level);
      if (diff > 1) {
        issues.push(`Energy ${track.metadata.energy_level} differs from target ${segment.energy_level}`);
      }
    }

    return issues;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlaylistAdd />
          <Typography variant="h6">Add Tracks to Segment</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Target: {segment.bpm_min}-{segment.bpm_max} BPM, Energy {segment.energy_level}/10
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          placeholder="Search by title, artist, or album..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : availableTracks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {searchQuery ? 'No matching tracks found' : 'No approved audio content available'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {availableTracks.map((track: any) => {
              const issues = getMatchStatus(track);
              const isSelected = selectedTracks.includes(track.id);

              return (
                <ListItem
                  key={track.id}
                  sx={{
                    bgcolor: isSelected ? 'action.selected' : 'background.paper',
                    mb: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: issues.length > 0 ? '1px solid' : 'none',
                    borderColor: 'warning.light',
                  }}
                  onClick={() => handleToggleTrack(track.id)}
                >
                  <Checkbox checked={isSelected} sx={{ mr: 1 }} />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {track.title || 'Unknown'}
                        {issues.length === 0 && (
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        )}
                        {issues.length > 0 && (
                          <Tooltip title={issues.join(', ')}>
                            <Warning sx={{ fontSize: 16, color: 'warning.main' }} />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={`${track.artist || 'Unknown Artist'} - ${formatDuration(track.duration)}`}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {track.metadata?.tempo && (
                      <Chip size="small" label={`${Math.round(track.metadata.tempo)} BPM`} />
                    )}
                    {track.metadata?.energy_level && (
                      <Chip size="small" label={`E${track.metadata.energy_level}`} />
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAddSelected}
          disabled={selectedTracks.length === 0 || addTrackMutation.isPending}
          startIcon={addTrackMutation.isPending ? <CircularProgress size={16} /> : <Add />}
        >
          Add {selectedTracks.length > 0 ? `(${selectedTracks.length})` : ''} Tracks
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Segment Detail Dialog Component
const SegmentDetailDialog: React.FC<{
  segmentId: string;
  onClose: () => void;
}> = ({ segmentId, onClose }) => {
  const { data: segment, isLoading } = useSegment(segmentId);
  const { data: analysis } = useSegmentAnalysis(segmentId);
  const autoFillMutation = useAutoFillSegment();
  const removeTrackMutation = useRemoveTrackFromSegment();
  const reorderMutation = useReorderSegmentTracks();

  const [addTrackOpen, setAddTrackOpen] = useState(false);
  const [tracks, setTracks] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Sync tracks state with segment data
  useEffect(() => {
    if (segment?.tracks) {
      setTracks(segment.tracks);
    }
  }, [segment?.tracks]);

  const handleAutoFill = async () => {
    try {
      await autoFillMutation.mutateAsync({ segmentId, maxTracks: 10 });
      setSnackbar({ open: true, message: 'Tracks added successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to auto-fill', severity: 'error' });
    }
  };

  const handleRemoveTrack = async (trackId: string) => {
    try {
      await removeTrackMutation.mutateAsync({ segmentId, trackId });
      setSnackbar({ open: true, message: 'Track removed', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove track', severity: 'error' });
    }
  };

  const handleReorder = async (newOrder: any[]) => {
    setTracks(newOrder);
    try {
      await reorderMutation.mutateAsync({
        segmentId,
        itemIds: newOrder.map(t => t.id),
      });
    } catch (err) {
      // Revert on error
      if (segment?.tracks) {
        setTracks(segment.tracks);
      }
      setSnackbar({ open: true, message: 'Failed to reorder tracks', severity: 'error' });
    }
  };

  if (isLoading || !segment) {
    return (
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const existingTrackIds = tracks.map(t => t.content_id);

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Extension sx={{ color: segment.color }} />
            <Typography variant="h6" sx={{ flex: 1 }}>{segment.name}</Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Analysis Panel */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Analytics /> Analysis
                </Typography>

                {analysis && (
                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">Match Score</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={analysis.meets_criteria ? 100 : Math.max(50, 100 - (analysis.issues?.length || 0) * 15)}
                          color={analysis.meets_criteria ? 'success' : 'warning'}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2">
                          {analysis.meets_criteria ? '100%' : `${Math.max(50, 100 - (analysis.issues?.length || 0) * 15)}%`}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Target BPM</Typography>
                        <Typography variant="body2">{segment.bpm_min}-{segment.bpm_max}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Actual BPM</Typography>
                        <Typography variant="body2">
                          {analysis.actual?.avg_bpm ? Math.round(analysis.actual.avg_bpm) : '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Target Energy</Typography>
                        <Typography variant="body2">{segment.energy_level}/10</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Actual Energy</Typography>
                        <Typography variant="body2">
                          {analysis.actual?.avg_energy ? analysis.actual.avg_energy.toFixed(1) : '-'}/10
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Tracks</Typography>
                        <Typography variant="body2">
                          {analysis.actual?.track_count || 0} / {segment.target_track_count || 8}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Duration</Typography>
                        <Typography variant="body2">
                          {analysis.actual?.total_duration_seconds
                            ? `${Math.round(analysis.actual.total_duration_seconds / 60)} min`
                            : '-'}
                        </Typography>
                      </Box>
                    </Box>

                    {analysis.issues?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="warning.main">Issues</Typography>
                        <List dense disablePadding>
                          {analysis.issues.map((issue: string, i: number) => (
                            <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                              <Warning sx={{ fontSize: 14, color: 'warning.main', mr: 0.5 }} />
                              <ListItemText
                                primary={issue}
                                primaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AutoAwesome />}
                  onClick={handleAutoFill}
                  disabled={autoFillMutation.isPending}
                  sx={{ mt: 2 }}
                >
                  {autoFillMutation.isPending ? 'Filling...' : 'Auto-Fill'}
                </Button>
              </Paper>
            </Grid>

            {/* Tracks Panel */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MusicNote /> Tracks ({tracks.length})
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setAddTrackOpen(true)}
                  >
                    Add Track
                  </Button>
                </Box>

                {tracks.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <MusicNote sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">No tracks yet</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Use Auto-Fill or click "Add Track" to browse content
                    </Typography>
                    <Button variant="outlined" onClick={() => setAddTrackOpen(true)} startIcon={<Add />}>
                      Add Track
                    </Button>
                  </Box>
                ) : (
                  <Reorder.Group
                    axis="y"
                    values={tracks}
                    onReorder={handleReorder}
                    style={{ listStyle: 'none', padding: 0, margin: 0 }}
                  >
                    {tracks.map((track) => (
                      <DraggableTrackItem
                        key={track.id}
                        track={track}
                        segment={segment}
                        onRemove={handleRemoveTrack}
                        isRemoving={removeTrackMutation.isPending}
                        formatDuration={formatDuration}
                      />
                    ))}
                  </Reorder.Group>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Add Track Dialog */}
      <AddTrackDialog
        open={addTrackOpen}
        onClose={() => setAddTrackOpen(false)}
        segmentId={segmentId}
        segment={segment}
        existingTrackIds={existingTrackIds}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </>
  );
};

export default SegmentsPage;
